/**
 * Everything `/admin` asks the server, and what each answer means.
 *
 * The page below this is a form and four lists; every decision it makes is
 * about a *response*, and a response is the one thing a component test in a
 * repository with no DOM runner cannot hand it. So the calls live here, return
 * a discriminated outcome rather than throwing, and `client/test/adminApi.test.ts`
 * walks each branch against a fake fetcher.
 *
 * Three refusals are told apart on purpose, because they send a person to three
 * different places and a page that said "failed" to all three would be a page
 * somebody reloads forever:
 *
 *   unauthorised  — no cookie, or it has expired. Show the password form.
 *   unconfigured  — `ADMIN_PASSWORD` is not set on this server at all. A
 *                   password form here is a loop, so say so instead.
 *   error         — the bucket, the network, or a refusal the server phrased
 *                   itself. Show its sentence.
 *
 * The password itself is never held here. `logIn` is the editor's — one
 * implementation, one endpoint — and it trades the password for an http-only
 * cookie the page cannot read and every later request carries on its own.
 */

import type { SessionIndexEntry } from '@overcooked/shared'
import { logIn } from '../../editor/save'
import type { Fetcher } from '../../editor/save'
import type { BatchFilter } from '../../replay/sessionTabs'
import { inviteParam } from '../../replay/sessionTabs'

export { logIn }
export type { Fetcher }

/** `/admin`, and nothing below it. */
export const ADMIN_PATH = /^\/admin\/?$/

/** One row of the map table: the shipped presets and the saved kitchens. */
export interface MapRow {
  name: string
  enabled: boolean
  preset: boolean
  width: number
  height: number
  seats: number
  createdAt: string | null
  updatedAt: string | null
}

/** One open room, as `server/src/admin/overview.ts` describes it. */
export interface RoomRow {
  code: string
  mapName: string
  phase: 'lobby' | 'playing' | 'ended'
  seated: number
  connected: number
  playerCount: number
  roundsPlayed: number
  sessionId: string
  lastActivityAt: number
  /** The invite code the room was opened under; `null` is no batch. */
  invite: string | null
}

/** One invite code, as `server/src/invites/inviteStore.ts` lists it. */
export interface InviteRow {
  code: string
  note: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export type AdminOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; kind: 'unauthorised' | 'unconfigured' | 'error'; error: string }

interface ErrorBody {
  error?: string
  /** `DELETE /api/admin/maps/:name` names the rooms it refused for. */
  rooms?: string[]
}

async function readJson<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T
  } catch {
    return null
  }
}

/**
 * One request, one outcome.
 *
 * A 401 is `unauthorised` and a 503 is `unconfigured` wherever they appear,
 * which is what lets every caller hand its outcome straight to the page without
 * each of them re-deciding what those two mean.
 */
async function call<T>(
  url: string,
  init: RequestInit,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<T>> {
  let res: Response
  try {
    res = await fetcher(url, { credentials: 'same-origin', ...init })
  } catch (err) {
    return { ok: false, kind: 'error', error: `Could not reach the server (${(err as Error).message}). Check your connection.` }
  }
  if (res.status === 401) return { ok: false, kind: 'unauthorised', error: 'Please log in to see this.' }
  const body = await readJson<T & ErrorBody>(res)
  if (res.status === 503) {
    return {
      ok: false,
      kind: 'unconfigured',
      error: body?.error ?? 'Admin is turned off on this server.',
    }
  }
  if (!res.ok) {
    return { ok: false, kind: 'error', error: body?.error ?? `The server returned an error (${res.status}).` }
  }
  if (body === null) return { ok: false, kind: 'error', error: "The server's answer couldn't be read. Try again." }
  return { ok: true, value: body }
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
})

export async function fetchMaps(fetcher: Fetcher = fetch): Promise<AdminOutcome<MapRow[]>> {
  const outcome = await call<{ maps: MapRow[] }>('/api/admin/maps', { method: 'GET' }, fetcher)
  return outcome.ok ? { ok: true, value: outcome.value.maps } : outcome
}

export async function fetchRooms(fetcher: Fetcher = fetch): Promise<AdminOutcome<RoomRow[]>> {
  const outcome = await call<{ rooms: RoomRow[] }>('/api/admin/rooms', { method: 'GET' }, fetcher)
  return outcome.ok ? { ok: true, value: outcome.value.rooms } : outcome
}

/**
 * The replay list.
 *
 * Read from `/api/admin/sessions` rather than from `/api/sessions` so that one
 * page asks one prefix for everything it shows; the two serve the same index and
 * are behind the same cookie. `/replay/<id>` itself is not — that link works for
 * whoever it is sent to, which is how a pair is shown their own round.
 *
 * `filter.invite` is the batch picker: a code, `''` for "No batch", or left out
 * for every round — which is the request this always made. The fetcher stays
 * the first argument so every existing caller reads the same.
 */
export async function fetchSessions(
  fetcher: Fetcher = fetch,
  filter: { invite?: BatchFilter } = {},
): Promise<AdminOutcome<SessionIndexEntry[]>> {
  const batch = inviteParam(filter.invite)
  const outcome = await call<{ sessions: SessionIndexEntry[] }>(
    batch ? `/api/admin/sessions?${batch}` : '/api/admin/sessions',
    { method: 'GET' },
    fetcher,
  )
  return outcome.ok ? { ok: true, value: outcome.value.sessions } : outcome
}

/**
 * What the recorded-rounds list says about consent for one round.
 *
 * `consent v1 · 2 of 2 players` names the wording agreed to and how many of the
 * people in the round had agreed. A round with nobody to ask — all agents, or
 * a solo round — says so; a row written before consent was recorded says that.
 */
export function consentLine(session: Partial<Pick<SessionIndexEntry, 'consent' | 'playerCount'>>): string {
  const consent = session.consent
  if (!consent) return 'consent not recorded'
  if (consent.humans === 0) return 'no players to ask'
  // A solo round records no voice and asks nothing.
  if (session.playerCount === 1 && consent.agreed === 0) return 'solo · no consent needed'
  if (consent.agreed === 0) return `no consent · 0 of ${consent.humans} ${players(consent.humans)}`
  const versions = consent.versions.map((v) => `v${v}`).join(', ')
  return `consent ${versions} · ${consent.agreed} of ${consent.humans} ${players(consent.humans)}`
}

const players = (n: number): string => (n === 1 ? 'player' : 'players')

export async function listInvites(fetcher: Fetcher = fetch): Promise<AdminOutcome<InviteRow[]>> {
  const outcome = await call<{ invites: InviteRow[] }>('/api/admin/invites', { method: 'GET' }, fetcher)
  return outcome.ok ? { ok: true, value: outcome.value.invites } : outcome
}

/**
 * Open a batch. The server normalises the code (trimmed, upper-cased) and
 * refuses a malformed one (400) or one that exists (409) in its own words.
 */
export async function createInvite(
  code: string,
  note: string,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<InviteRow>> {
  const outcome = await call<{ invite: InviteRow }>('/api/admin/invites', json({ code, note }), fetcher)
  return outcome.ok ? { ok: true, value: outcome.value.invite } : outcome
}

/** Switch a batch on or off. There is no delete: old sessions still name it. */
export async function setInviteEnabled(
  code: string,
  enabled: boolean,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<InviteRow>> {
  const outcome = await call<{ invite: InviteRow }>(
    `/api/admin/invites/${encodeURIComponent(code)}`,
    { ...json({ enabled }), method: 'PATCH' },
    fetcher,
  )
  return outcome.ok ? { ok: true, value: outcome.value.invite } : outcome
}

/** The show/hide switch. A built-in kitchen may be hidden too. */
export function setMapEnabled(
  name: string,
  enabled: boolean,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<{ name: string; enabled: boolean }>> {
  return call(
    `/api/admin/maps/${encodeURIComponent(name)}`,
    { ...json({ enabled }), method: 'PATCH' },
    fetcher,
  )
}

/**
 * Forget a saved kitchen.
 *
 * Refused for a shipped preset and for a map a room is still standing on; both
 * come back as `error` carrying the server's own sentence, which names the rooms
 * in the second case. Nothing is decided again here — the server is the only
 * place that can see either fact.
 */
export function deleteMap(
  name: string,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<{ name: string; removed: true }>> {
  return call(`/api/admin/maps/${encodeURIComponent(name)}`, { method: 'DELETE' }, fetcher)
}

/** Throw the cookie away. Answers 200 even on a server with no password. */
export async function logOut(fetcher: Fetcher = fetch): Promise<void> {
  try {
    await fetcher('/api/admin/session', { method: 'DELETE', credentials: 'same-origin' })
  } catch {
    // A log-out that cannot reach the server has still ended this page's session
    // as far as the person pressing it is concerned; the cookie expires on its
    // own. Nothing here is worth an error panel.
  }
}

/** Open a room on a map, enabled or not — the way a draft gets playtested. */
export async function openRoom(
  map: string,
  fetcher: Fetcher = fetch,
): Promise<AdminOutcome<{ code: string; mapName: string }>> {
  const outcome = await call<{ code: string; mapName: string }>('/api/rooms', json({ map }), fetcher)
  if (!outcome.ok) return outcome
  // `mintRoom` answers with the kitchen it resolved, which is the default one
  // when it did not recognise the name. Reporting that is the whole reason this
  // reads the response rather than just the status.
  if (outcome.value.mapName !== map) {
    return {
      ok: false,
      kind: 'error',
      error: `The game opened on "${outcome.value.mapName}" instead of "${map}". Check that the kitchen is saved.`,
    }
  }
  return outcome
}
